import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMyCompanies } from '@/features/companies/hooks/useCompanies'
import type { MeCompany } from '@/types/company.types'

export interface MyCompanyFilter {
  companies: MeCompany[]
  companiesLoading: boolean
  selectedCompanyIds: Set<string>
  /** `undefined` when nothing selected, so the API treats it as "all my companies". */
  companyIds: string[] | undefined
  companyNameById: Map<string, string>
  toggle: (companyId: string | null) => void
  reset: () => void
}

export interface UseMyCompanyFilterOptions {
  /** Called after every selection change. Use to reset pagination, etc. */
  onChange?: () => void
}

/**
 * Multi-select company filter for self-service ("My *") pages.
 *
 * Parallel to `useWorkspaceCompanyFilter` but sourced from `useMyCompanies()`,
 * so the two hooks share the same shape and semantics:
 * - `companyIds` is `undefined` when nothing is selected ("all my companies").
 * - Single-company users stay force-selected on their one company.
 * - Initial selection is seeded from `?companyId=` (single or comma-separated).
 */
export function useMyCompanyFilter(
  opts?: UseMyCompanyFilterOptions,
): MyCompanyFilter {
  const source = useMyCompanies()
  const companies = source.data ?? []
  const companiesLoading = source.isLoading
  const onlyCompanyId = companies.length === 1 ? companies[0]._id : null

  const [selectedCompanyIds, setSelected] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set()
    const v = new URLSearchParams(window.location.search).get('companyId')
    if (!v) return new Set()
    return new Set(v.split(',').map((s) => s.trim()).filter(Boolean))
  })

  const companyIds = useMemo(
    () =>
      selectedCompanyIds.size > 0 ? Array.from(selectedCompanyIds) : undefined,
    [selectedCompanyIds],
  )

  const companyNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const c of companies) m.set(c._id, c.name)
    return m
  }, [companies])

  const onChange = opts?.onChange

  useEffect(() => {
    if (!onlyCompanyId) return
    setSelected((prev) => {
      if (prev.size === 1 && prev.has(onlyCompanyId)) return prev
      return new Set([onlyCompanyId])
    })
  }, [onlyCompanyId])

  const toggle = useCallback(
    (companyId: string | null) => {
      if (companyId === null) {
        setSelected(onlyCompanyId ? new Set([onlyCompanyId]) : new Set())
      } else {
        setSelected((prev) => {
          const next = new Set(prev)
          if (next.has(companyId)) {
            if (onlyCompanyId === companyId) return next
            next.delete(companyId)
          } else {
            next.add(companyId)
          }
          return next
        })
      }
      onChange?.()
    },
    [onChange, onlyCompanyId],
  )

  const reset = useCallback(() => {
    setSelected(onlyCompanyId ? new Set([onlyCompanyId]) : new Set())
    onChange?.()
  }, [onChange, onlyCompanyId])

  return {
    companies,
    companiesLoading,
    selectedCompanyIds,
    companyIds,
    companyNameById,
    toggle,
    reset,
  }
}
